// Solutions section — full page content per capability

const build = (kicker, title, tagline, intro, pillars, stats = [], faqs = [], ctaHref = "/terminal") => ({
  kicker, title, tagline, intro, pillars, stats, faqs,
  cta: { title: "Initialize project.", sub: "Scope the engagement.", href: ctaHref },
});

const bullets = (arr) => arr.map((b) => (typeof b === "string" ? { title: b, desc: "" } : b));

export const solutionsContent = {
  "artificial-intelligence": build(
    "// SOL.AI",
    "Artificial Intelligence — architected, not adopted.",
    "Custom LLMs, agentic workflows, and evaluation harnesses that ship.",
    "We build production-grade AI systems that go beyond API wrappers. Every deployment is engineered with model risk documentation, evaluation suites, and human-in-the-loop protocols.",
    [
      { id: "01", label: "LLM.ORCHESTRATION", body: "Multi-provider LLM routing with fallback logic, prompt versioning, and cost-aware routing.", bullets: bullets(["Provider-agnostic layer", "Prompt version control", "Cost telemetry per call"]) },
      { id: "02", label: "AGENTIC.WORKFLOWS", body: "Autonomous agents for enterprise ops — with observability, guardrails, and rollback.", bullets: bullets(["Tool-use frameworks", "Guardrail policies", "Full audit trails"]) },
      { id: "03", label: "EVALUATION.HARNESSES", body: "Every model in production has a corresponding eval suite tied to business KPIs.", bullets: bullets(["Golden test sets", "Regression detection", "Drift alerts"]) },
    ],
    [{ value: "100%", label: "Models with eval suites" }, { value: "0", label: "API-wrapper-only builds" }]
  ),

  "cloud": build(
    "// SOL.CLOUD",
    "Cloud infrastructure that scales without surprises.",
    "AWS, GCP, and Azure — architected for isolation, cost control, and audit.",
    "Cloud is a foundation, not a feature. We design multi-tenant, multi-region cloud infrastructures with hardened defaults and cost telemetry from day one.",
    [
      { id: "01", label: "ARCHITECTURE", body: "Reference architectures for multi-tenant SaaS, single-tenant enterprise, and hybrid deployments.", bullets: bullets(["Kubernetes-native", "Zero-trust ingress", "Cross-region failover"]) },
      { id: "02", label: "COST.CONTROL", body: "FinOps built in — real-time cost telemetry with anomaly detection and budget enforcement.", bullets: bullets(["Per-tenant cost tracking", "Budget guardrails", "Optimization sprints"]) },
      { id: "03", label: "MIGRATION", body: "Lift-and-shift, replatform, or refactor — we scope migrations against real business outcomes.", bullets: bullets(["Zero-downtime cutovers", "Rollback plans", "Post-cutover audits"]) },
    ]
  ),

  "customer-experience": build(
    "// SOL.CX",
    "Customer experience, engineered end-to-end.",
    "Digital touchpoints that convert — instrumented like production systems.",
    "We treat CX as a product. Every touchpoint is measured, versioned, and optimized against real business metrics — not vanity dashboards.",
    [
      { id: "01", label: "JOURNEY.MAPPING", body: "Instrumented journey maps tied to real analytics streams.", bullets: bullets(["Cross-channel telemetry", "Conversion funnels", "Attribution modeling"]) },
      { id: "02", label: "DIGITAL.PRODUCTS", body: "Web, mobile, and in-app experiences engineered for velocity and conversion.", bullets: bullets(["Design system delivery", "Accessibility-first", "Perf budgets enforced"]) },
      { id: "03", label: "SERVICE.DESIGN", body: "End-to-end service design linking customer, employee, and system layers.", bullets: bullets(["Ops workflow redesign", "Frontline enablement"]) },
    ]
  ),

  "cybersecurity": build(
    "// SOL.CYBER",
    "Cybersecurity as an engineering discipline.",
    "Zero-trust architecture, hardened deployments, real audits.",
    "We treat security as a build problem, not a compliance checkbox. Every deployment ships with threat models, hardened defaults, and continuous audit.",
    [
      { id: "01", label: "ZERO.TRUST", body: "Zero-trust ingress, identity-aware proxies, and cryptographic service auth.", bullets: bullets(["Identity-native routing", "mTLS everywhere", "Short-lived credentials"]) },
      { id: "02", label: "HARDENED.DEPLOY", body: "Baseline images, SBOMs, and CVE monitoring across every environment.", bullets: bullets(["SBOM for every service", "Automated CVE alerts", "Immutable infra"]) },
      { id: "03", label: "INCIDENT.RESPONSE", body: "24/7 monitoring, tabletop exercises, and post-incident retros.", bullets: bullets(["Runbook library", "Quarterly tabletops"]) },
    ]
  ),

  "data-and-analytics": build(
    "// SOL.DATA",
    "Data platforms built for analysts, not architects.",
    "Warehouse, pipelines, and analytics — end-to-end.",
    "We build modern data stacks that scale from first metric to enterprise data mesh — with governance and lineage engineered in.",
    [
      { id: "01", label: "PIPELINES", body: "Batch and streaming pipelines with lineage, retries, and observability.", bullets: bullets(["dbt-native", "Streaming via Kafka/Kinesis", "Lineage capture"]) },
      { id: "02", label: "WAREHOUSE", body: "Snowflake, BigQuery, Databricks — schema-first, cost-conscious, well-modeled.", bullets: bullets(["Dimensional modeling", "Cost-optimized storage"]) },
      { id: "03", label: "ANALYTICS", body: "Self-serve BI, executive dashboards, and embedded analytics.", bullets: bullets(["Semantic layer", "Metric governance"]) },
    ]
  ),

  "data-privacy": build(
    "// SOL.PRIVACY",
    "Data privacy engineered into the architecture.",
    "GDPR, CCPA, and beyond — designed in, not bolted on.",
    "Privacy is a build constraint, not a legal afterthought. We engineer data-minimization, consent, and deletion into the platform itself.",
    [
      { id: "01", label: "DATA.MAPPING", body: "Full data inventory, classification, and lineage across all systems.", bullets: bullets(["Automated discovery", "Classification tagging"]) },
      { id: "02", label: "CONSENT.SYSTEMS", body: "Consent orchestration engineered for auditability.", bullets: bullets(["Preference stores", "Auditable events"]) },
      { id: "03", label: "DSR.OPERATIONS", body: "Data subject rights automation — deletion, export, correction.", bullets: bullets(["Automated fulfillment", "SLA compliance"]) },
    ]
  ),

  "digital-transformation": build(
    "// SOL.DX",
    "Transformation that leaves systems, not slideware.",
    "We ship the target state — we don't merely draw it.",
    "TraceElements-led transformation programs are engineered from operating-model design through platform delivery. Every roadmap is executed end-to-end.",
    [
      { id: "01", label: "TARGET.OPERATING.MODEL", body: "Design the future state with clear KPIs, teams, and platforms.", bullets: bullets(["Value-stream mapping", "Capability heatmaps"]) },
      { id: "02", label: "PLATFORM.BUILD", body: "Engineer the platforms the transformation depends on.", bullets: bullets(["Modern data stack", "Composable frontends"]) },
      { id: "03", label: "CHANGE.EXECUTION", body: "Adoption programs, training curricula, and rollout waves.", bullets: bullets(["Wave planning", "Enablement pods"]) },
    ]
  ),

  "ecosystem-partners": build(
    "// SOL.PARTNERS",
    "The ecosystem partners we deploy alongside.",
    "Selected integrations with best-in-class vendors.",
    "TraceElements maintains deep partnerships with a curated set of platform vendors. We select partners based on engineering quality — not commercial incentive.",
    [
      { id: "01", label: "CLOUD", body: "AWS, GCP, Azure — architecture partners.", bullets: bullets(["Well-Architected reviews", "Cost audits"]) },
      { id: "02", label: "DATA", body: "Snowflake, Databricks, dbt Labs — implementation partners.", bullets: bullets(["Certified engineers"]) },
      { id: "03", label: "AI", body: "OpenAI, Anthropic, Google — enterprise deployment partners.", bullets: bullets(["Enterprise contract support"]) },
    ]
  ),

  "finance-transformation": build(
    "// SOL.FIN",
    "Finance transformation with real platform delivery.",
    "Close cycles, FP&A, and financial data engineering.",
    "We reduce close cycles, automate reconciliations, and modernize FP&A stacks — with the same engineering discipline we apply to product platforms.",
    [
      { id: "01", label: "CLOSE.AUTOMATION", body: "Reduce monthly and quarterly close cycles through automation and integration.", bullets: bullets(["Reconciliation bots", "Auto-journals"]) },
      { id: "02", label: "FP&A.MODERNIZATION", body: "Modern planning stack — driver-based, integrated, real-time.", bullets: bullets(["Anaplan/Pigment/Cube", "Driver-based models"]) },
      { id: "03", label: "FINANCIAL.DATA", body: "Financial data warehousing with GL-grade governance.", bullets: bullets(["GL reconciliation", "Audit trail"]) },
    ]
  ),

  "internal-audit": build(
    "// SOL.AUDIT",
    "Internal audit — modernized for real-time enterprises.",
    "Continuous auditing, risk analytics, and audit engineering.",
    "We modernize internal audit functions with data-driven risk analytics, continuous monitoring, and engineered evidence collection.",
    [
      { id: "01", label: "CONTINUOUS.AUDIT", body: "Move from annual point-in-time audits to continuous, data-driven assurance.", bullets: bullets(["Real-time monitoring", "Anomaly detection"]) },
      { id: "02", label: "RISK.ANALYTICS", body: "Risk-scoring models across financial, operational, and cyber domains.", bullets: bullets(["Composite risk scoring"]) },
      { id: "03", label: "AUDIT.ENGINEERING", body: "Automated evidence collection and workpaper generation.", bullets: bullets(["Automated workpapers"]) },
    ]
  ),

  "legal-consulting": build(
    "// SOL.LEGAL",
    "Legal operations, digitized.",
    "Contract engineering, e-discovery, and legal ops modernization.",
    "We engineer legal operations — from contract lifecycle automation to e-discovery pipelines and legal knowledge platforms.",
    [
      { id: "01", label: "CONTRACT.LIFECYCLE", body: "Modern CLM with AI-assisted review and standardized clause libraries.", bullets: bullets(["Clause library", "AI first-pass review"]) },
      { id: "02", label: "E-DISCOVERY", body: "Engineered e-discovery pipelines with lineage, chain of custody, and analytics.", bullets: bullets(["Chain of custody", "Analytics"]) },
      { id: "03", label: "LEGAL.OPS", body: "Matter management, spend analytics, and vendor governance.", bullets: bullets(["Spend telemetry", "Vendor scorecards"]) },
    ]
  ),

  "managed-solutions": build(
    "// SOL.MANAGED",
    "Managed solutions — long-arc operating partnership.",
    "We run the systems we build.",
    "For select clients, we operate the platforms we've engineered — with SLAs, monitoring, and long-term evolution baked in.",
    [
      { id: "01", label: "OPERATE", body: "24/7 monitoring, on-call rotations, incident response.", bullets: bullets(["Full observability", "SLA-backed uptime"]) },
      { id: "02", label: "EVOLVE", body: "Quarterly evolution sprints keep the platform aligned to business change.", bullets: bullets(["Quarterly roadmap reviews"]) },
      { id: "03", label: "AUDIT", body: "Continuous audit of security posture, cost efficiency, and technical debt.", bullets: bullets(["Audit reports quarterly"]) },
    ]
  ),

  "operations": build(
    "// SOL.OPS",
    "Operations engineering for elite scale.",
    "Process design, automation, and telemetry.",
    "We treat operations as a product — designed, instrumented, and continuously improved.",
    [
      { id: "01", label: "PROCESS.DESIGN", body: "Map, measure, and rebuild core operating processes.", bullets: bullets(["Value-stream mapping", "Bottleneck analysis"]) },
      { id: "02", label: "AUTOMATION", body: "RPA, workflow automation, and AI-assisted process execution.", bullets: bullets(["Workflow orchestration", "AI-in-the-loop"]) },
      { id: "03", label: "TELEMETRY", body: "Operational dashboards tied to real business KPIs.", bullets: bullets(["Live KPI dashboards"]) },
    ]
  ),

  "people-and-change": build(
    "// SOL.PEOPLE",
    "People and change — engineered for adoption.",
    "Change programs measured like product launches.",
    "We treat change management as an engineering problem. Adoption is measured, iterated, and shipped.",
    [
      { id: "01", label: "CHANGE.ARCHITECTURE", body: "Design change programs with clear KPIs, wave plans, and stakeholder maps.", bullets: bullets(["Stakeholder mapping", "Wave sequencing"]) },
      { id: "02", label: "ENABLEMENT", body: "Training curricula, certification paths, and in-product enablement.", bullets: bullets(["Certification programs"]) },
      { id: "03", label: "ADOPTION.TELEMETRY", body: "Measure adoption in real time — not by survey.", bullets: bullets(["Adoption dashboards"]) },
    ]
  ),

  "quantum-computing": build(
    "// SOL.QUANTUM",
    "Quantum computing — real experiments, not hype.",
    "Hybrid classical-quantum workloads, benchmarked honestly.",
    "We help clients evaluate where quantum computing offers real advantage — and where classical infrastructure remains the correct answer.",
    [
      { id: "01", label: "USE.CASE.EVAL", body: "Rigorous evaluation of candidate workloads against quantum-suitability criteria.", bullets: bullets(["QAOA feasibility", "Cost-benefit modeling"]) },
      { id: "02", label: "HYBRID.WORKLOADS", body: "Design hybrid pipelines combining classical and quantum layers.", bullets: bullets(["Provider-agnostic", "Benchmark-driven"]) },
      { id: "03", label: "READINESS", body: "Post-quantum cryptography readiness for security-sensitive stacks.", bullets: bullets(["PQC migration paths"]) },
    ]
  ),

  "regulatory-compliance": build(
    "// SOL.REGCOMP",
    "Regulatory compliance — engineered into the platform.",
    "SOC 2, HIPAA, PCI, GDPR, and beyond.",
    "We engineer compliance into architecture, not around it. Every managed platform ships with continuous compliance monitoring.",
    [
      { id: "01", label: "READINESS", body: "Gap assessments, control mapping, and remediation roadmaps.", bullets: bullets(["Control mapping"]) },
      { id: "02", label: "IMPLEMENTATION", body: "Engineer the controls, evidence collection, and monitoring.", bullets: bullets(["Automated evidence"]) },
      { id: "03", label: "SUSTAINMENT", body: "Continuous compliance monitoring and audit-ready posture.", bullets: bullets(["Audit-ready always"]) },
    ]
  ),

  "risk-management": build(
    "// SOL.RISK",
    "Enterprise risk management, quantified.",
    "Risk scoring, scenario modeling, and continuous monitoring.",
    "We modernize enterprise risk with quantitative models, live telemetry, and executive-grade dashboards.",
    [
      { id: "01", label: "RISK.QUANTIFICATION", body: "Move from heat maps to real quantitative risk models.", bullets: bullets(["FAIR-based modeling", "Monte Carlo"]) },
      { id: "02", label: "SCENARIO.PLANNING", body: "Live scenario modeling for financial, cyber, and operational risk.", bullets: bullets(["Cross-domain scenarios"]) },
      { id: "03", label: "REPORTING", body: "Executive risk dashboards with drill-through to underlying signal.", bullets: bullets(["Board-grade reporting"]) },
    ]
  ),

  "sarbanes-oxley": build(
    "// SOL.SOX",
    "Sarbanes-Oxley — modernized and continuously monitored.",
    "SOX compliance engineered like a product.",
    "We help public companies modernize SOX compliance — reducing testing hours, automating control evidence, and enabling continuous assurance.",
    [
      { id: "01", label: "CONTROL.RATIONALIZATION", body: "Reduce redundant controls without eroding assurance.", bullets: bullets(["Control mapping", "Redundancy elimination"]) },
      { id: "02", label: "EVIDENCE.AUTOMATION", body: "Automated evidence collection for ITGCs and application controls.", bullets: bullets(["Automated ITGC"]) },
      { id: "03", label: "CONTINUOUS.MONITORING", body: "Move from quarterly testing to always-on monitoring.", bullets: bullets(["Live control status"]) },
    ]
  ),

  "sustainability": build(
    "// SOL.SUSTAIN",
    "Sustainability — measured, reported, and improved.",
    "ESG data engineering and carbon accounting.",
    "We engineer the data infrastructure that sustainability reporting demands — auditable, complete, and connected to real operational systems.",
    [
      { id: "01", label: "ESG.DATA", body: "Data pipelines for scope 1/2/3 emissions, energy, and supplier data.", bullets: bullets(["Scope 1/2/3 pipelines"]) },
      { id: "02", label: "REPORTING", body: "Regulatory-grade reporting for CSRD, SEC climate, and voluntary frameworks.", bullets: bullets(["CSRD-ready", "TCFD alignment"]) },
      { id: "03", label: "OPERATIONS", body: "Cloud carbon optimization and green architecture reviews.", bullets: bullets(["Cloud carbon audit"]) },
    ]
  ),

  "technology": build(
    "// SOL.TECH",
    "Technology strategy that ships.",
    "From architecture to platform delivery.",
    "TraceElements' technology practice bridges strategy and execution — because our strategists build the platforms they design.",
    [
      { id: "01", label: "ARCHITECTURE", body: "Enterprise architecture, reference designs, and technology roadmaps.", bullets: bullets(["Ref architectures", "Roadmap sequencing"]) },
      { id: "02", label: "PLATFORM.DELIVERY", body: "Engineer the platforms the strategy depends on.", bullets: bullets(["Composable platforms"]) },
      { id: "03", label: "MODERNIZATION", body: "Application modernization, technical debt reduction, and platform re-platforming.", bullets: bullets(["Strangler-fig migrations"]) },
    ]
  ),

  "transaction-services": build(
    "// SOL.TXN",
    "Transaction services — tech-enabled diligence and integration.",
    "Diligence, carve-outs, and post-merger integration.",
    "For private equity and corporate M&A teams, we deliver tech-enabled diligence, TSA design, and post-merger integration programs.",
    [
      { id: "01", label: "DILIGENCE", body: "Tech, cyber, and operational diligence with data-driven findings.", bullets: bullets(["Tech due diligence", "Cyber DD"]) },
      { id: "02", label: "CARVE-OUT", body: "TSA design and standalone platform engineering.", bullets: bullets(["TSA construction", "Day-1 readiness"]) },
      { id: "03", label: "INTEGRATION", body: "Post-merger integration roadmaps and platform consolidation.", bullets: bullets(["PMI wave plans"]) },
    ]
  ),
};
